import type { MapRef } from "@vis.gl/react-maplibre";
import {
  AttributionControl,
  GeolocateControl,
  Map,
  NavigationControl,
} from "@vis.gl/react-maplibre";
import type { LngLatBoundsLike } from "maplibre-gl";
import { forwardRef } from "react";
import { MAP_STYLE, SWEDEN_BOUNDS, SWEDEN_INITIAL_VIEW } from "../../lib/mapConfig";

type ControlCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface BaseMapProps {
  children?: React.ReactNode;
  /** CSS class applied to the outer wrapper div. */
  className?: string;
  /** Override initial viewport. Defaults to Sweden overview. */
  initialLongitude?: number;
  initialLatitude?: number;
  initialZoom?: number;
  /** When true, disables interactive controls (drag, zoom, etc). */
  staticMap?: boolean;
  /** Override the basemap style.json URL. Defaults to MAP_STYLE. */
  mapStyle?: string;
  /** Override maxBounds (pan/zoom-out limit). Defaults to SWEDEN_BOUNDS;
   *  pages that need to zoom out further (e.g. the regions overview on
   *  short viewports) can pass a wider rectangle. */
  maxBounds?: LngLatBoundsLike;
  /** Corner for the attribution control. Defaults to bottom-left on
   *  interactive maps (top edge is reserved for the centred MapDayTag,
   *  top-right has the zoom controls) and bottom-right on static maps
   *  (where the compass overlay takes the top/centre). */
  attributionPosition?: ControlCorner;
  /** When true (and the map is interactive), render MapLibre's built-in
   *  GeolocateControl. Triggers the browser geolocation prompt and pans
   *  to the user's position with a default blue dot + accuracy circle. */
  showGeolocate?: boolean;
  /** Keep the WebGL drawing buffer so the canvas can be read back into an
   *  image (e.g. html-to-image PNG export on /dev/social). Off by default —
   *  it has a small perf cost and is only needed for off-screen capture. */
  preserveDrawingBuffer?: boolean;
  onLoad?: () => void;
}

export const BaseMap = forwardRef<MapRef, BaseMapProps>(function BaseMap(
  {
    children,
    className,
    initialLongitude = SWEDEN_INITIAL_VIEW.longitude,
    initialLatitude = SWEDEN_INITIAL_VIEW.latitude,
    initialZoom = SWEDEN_INITIAL_VIEW.zoom,
    staticMap = false,
    mapStyle = MAP_STYLE,
    maxBounds = SWEDEN_BOUNDS,
    attributionPosition,
    showGeolocate = false,
    preserveDrawingBuffer = false,
    onLoad,
  },
  ref,
) {
  const attribPos: ControlCorner =
    attributionPosition ?? (staticMap ? "bottom-right" : "bottom-left");
  return (
    <div className={className} style={{ position: "relative", width: "100%", height: "100%" }}>
      <Map
        ref={ref}
        mapStyle={mapStyle}
        initialViewState={{
          longitude: initialLongitude,
          latitude: initialLatitude,
          zoom: initialZoom,
        }}
        {...(!staticMap && { maxBounds })}
        minZoom={3}
        maxZoom={14}
        dragRotate={false}
        pitchWithRotate={false}
        touchPitch={false}
        scrollZoom={!staticMap}
        dragPan={!staticMap}
        keyboard={!staticMap}
        doubleClickZoom={!staticMap}
        touchZoomRotate={!staticMap}
        {...(preserveDrawingBuffer && {
          canvasContextAttributes: { preserveDrawingBuffer: true },
        })}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        {...(onLoad !== undefined && { onLoad })}
      >
        {/* On interactive maps the top edge is reserved for the centred
            MapDayTag (echoing the day strip's selection), and the zoom
            controls sit at top-right — so attribution drops to the
            bottom-left, behind the floating MapSpotCard when one is
            open. Static maps keep attribution at bottom-right since
            the compass overlay owns the centre. */}
        <AttributionControl position={attribPos} compact />
        {!staticMap && <NavigationControl position="top-right" showCompass={false} />}
        {!staticMap && showGeolocate && (
          <GeolocateControl
            position="top-right"
            positionOptions={{ enableHighAccuracy: true }}
            trackUserLocation
          />
        )}
        {children}
      </Map>
    </div>
  );
});
